import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { describeCrmOutcomes } from "@/v2-backoffice-client/crm/crm.outcome.utils"
import { useTenantInvitationCreateManyMutation } from "@/v2-backoffice-client/tenant/invitation/create/tenant.invitation.create.many.mutation"
import TenantInvitationCreateForm, {
  type TenantInvitationCreateFormData,
} from "@/v2-backoffice-client/tenant/invitation/create/TenantInvitationCreateForm"
import type { InvitationEmailTemplateValue } from "@/v2-backoffice-client/invitation/invitation.constants"
import UserMigratePromptDialog from "@/v2-backoffice-client/user/migration/prompt/UserMigratePromptDialog"
import {
  useUserMigratePrompt,
  withoutPromptedUsers,
} from "@/v2-backoffice-client/user/migration/prompt/user.migration.prompt.hooks"

export type TenantInvitationCreateDialogProps = DialogProps & {
  tenantId: number
}

function TenantInvitationCreateDialog(
  props: TenantInvitationCreateDialogProps,
) {
  const { tenantId, open, onOpenChange, ...dialogProps } = props

  const [createManyInvitations, { loading, error }] =
    useTenantInvitationCreateManyMutation()
  // Existing-but-not-activated users among the invitees get the login
  // migration instead of an invitation (UserMigratePromptDialog).
  const migratePrompt = useUserMigratePrompt<TenantInvitationCreateFormData>()

  async function send(data: TenantInvitationCreateFormData) {
    const result = await createManyInvitations({
      variables: {
        inputs: data.rows.map((row) => ({
          TenantId: tenantId,
          ProjectId: data.ProjectId,
          AutoCreateProject: data.AutoCreateProject,
          CountryCode: data.CountryCode,
          Email: row.Email,
          FirstName: row.FirstName,
          LastName: row.LastName,
          UserTenantTypeCode: row.UserTenantTypeCode,
          MinuteLimit: row.MinuteLimit,
          SendImmediately: data.SendImmediately,
          Language: data.Language,
          // The literal→generated-enum bridge (see invitation.constants).
          EmailTemplate: data.EmailTemplate as InvitationEmailTemplateValue,
        })),
        crm: { CreateContact: data.CreateContact },
      },
    })
    // errorPolicy is "all" app-wide, so a rejected batch (project not
    // linked to the team, unsupported country, …) resolves instead of
    // throwing — closing here would swallow the error banner above.
    if (result.errors?.length) return
    onOpenChange?.(false)

    const created = result.data?.createManyUserInvitations.Invitations ?? []
    const crmSummary = describeCrmOutcomes(
      result.data?.createManyUserInvitations.Crm ?? [],
    )
    toast({
      variant: "success",
      title: `${created.length} invitation${created.length === 1 ? "" : "s"} sent`,
      description:
        crmSummary ||
        "Invitees are on their HubSpot contacts as soon as the hourly sync runs.",
    })
  }

  const prompt = migratePrompt.prompt
  const remainingRows = prompt
    ? withoutPromptedUsers(prompt.data.rows, prompt.users)
    : []

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
        <Dialog.Content
          className="w-11/12 max-w-screen-md !top-[6vh]"
          align="top"
        >
          <Dialog.Header>
            <Dialog.Title>Invite Users</Dialog.Title>
            <Dialog.Description>
              Send invitation emails to add new users to this tenant. You can
              paste multiple emails at once.
            </Dialog.Description>
          </Dialog.Header>

          <ErrorMessage className="my-4 text-center" error={error} />

          <TenantInvitationCreateForm
            id="tenant-invitation-create-form"
            tenantId={tenantId}
            onSubmit={async (data, e) => {
              e.preventDefault()
              const emails = data.rows.map((row) => row.Email)
              if (await migratePrompt.intercept(emails, data)) return
              await send(data)
            }}
          />

          <Dialog.Footer className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange?.(false)
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              form="tenant-invitation-create-form"
              isLoading={loading || migratePrompt.checking}
            >
              {loading ? "Sending..." : "Send Invitations"}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>

      <UserMigratePromptDialog
        open={prompt !== null}
        onOpenChange={(next) => {
          if (!next) migratePrompt.dismiss()
        }}
        action="invite"
        users={prompt?.users ?? []}
        remainingCount={remainingRows.length}
        onProceedAnyway={() => {
          if (!prompt) return
          migratePrompt.dismiss()
          void send(prompt.data)
        }}
        onSendActivation={() => {
          if (!prompt) return
          migratePrompt.dismiss()
          if (remainingRows.length > 0) {
            void send({ ...prompt.data, rows: remainingRows })
          } else {
            onOpenChange?.(false)
          }
        }}
      />
    </>
  )
}

export default TenantInvitationCreateDialog
